import { useSelector } from 'react-redux'
import { creditsSelectors } from 'store/features/credits/selectors'
import { useOpenUpgradePopup } from 'hooks/useOpenUpgradePopup'
import { REF_LINKS } from 'consts/refLinks'
import { THUNDER_ICON } from 'consts/mediaUrls'
import { CrownIcon } from 'containers/SubSidebar/styled'
import * as Styled from './styled'

interface IAvatarActionFlyoutPanelProps {
  title: string
  // Enhance passes descriptive text; Resize omits it and uses `children`.
  description?: string
  // Body slot — Resize passes its aspect-ratio selector here.
  children?: React.ReactNode
  ctaLabel: string
  cost: number
  // Grays out the CTA and ignores clicks — Resize uses it until the user
  // picks an aspect ratio.
  isCtaDisabled?: boolean
  onConfirm: () => void
}

// Shared content shell for the Enhance/Resize flyouts: title + (text | body) +
// a CTA footer with the thunder badge (no cost number — free users never reach
// this panel, the icon click is gated in AvatarActionFlyoutButton). No X
// button: the flyout closes on outside click / Esc (desktop) or the SlideUp
// drag-line (mobile). Insufficient credits routes the CTA to the refill popup
// instead of confirming, mirroring the AI Image generate footer.
const AvatarActionFlyoutPanel = ({
  title,
  description,
  children,
  ctaLabel,
  cost,
  isCtaDisabled,
  onConfirm
}: IAvatarActionFlyoutPanelProps) => {
  const userCredits = useSelector(creditsSelectors.userCredits) || 0
  const hasInsufficientCredits = userCredits < cost
  const openUpgradePopup = useOpenUpgradePopup({ usePlanCheck: true })

  const handleCta = () => {
    if (isCtaDisabled) return
    if (hasInsufficientCredits) {
      openUpgradePopup({ ref: REF_LINKS.V_EDITOR_AI_AVATAR_REFILL })
      return
    }
    onConfirm()
  }

  return (
    <Styled.PanelContainer $hasDescription={!!description}>
      <Styled.PanelTitle>{title}</Styled.PanelTitle>
      {description && <Styled.PanelText>{description}</Styled.PanelText>}
      {children}
      <Styled.CtaButton
        onClick={handleCta}
        isDisabled={isCtaDisabled}
        $isPremium={hasInsufficientCredits}
      >
        <Styled.ButtonContent>
          {hasInsufficientCredits && <CrownIcon />}
          {ctaLabel}
          <Styled.CreditsBadge>
            <Styled.ThunderIcon src={THUNDER_ICON} alt="credits" />
          </Styled.CreditsBadge>
        </Styled.ButtonContent>
      </Styled.CtaButton>
    </Styled.PanelContainer>
  )
}

export default AvatarActionFlyoutPanel
